import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, Search, User, LogOut, Settings, Menu, X, Home, ShoppingBag, Users, UsersRound, MessageCircle, Church, Bookmark, Heart, Cross, Building2, Shield, Package, BarChart3, UserPlus, Check, Ticket, HelpCircle, Bug, Lightbulb, MessageSquare, Globe, HandHeart, Megaphone, Calendar, Newspaper, Star, BookOpen, ListChecks, MapPinned } from "lucide-react";

// Inline Halo icon to avoid babel plugin issues with custom icon imports
function HaloIcon({ className = "", size = 24, ...props }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <ellipse cx="12" cy="9" rx="9.5" ry="3.5" transform="rotate(-8 12 9)" />
      <ellipse cx="12" cy="9" rx="7" ry="2.2" transform="rotate(-8 12 9)" opacity="0.4" />
    </svg>
  );
}
import { logoutUser, getNotifications, getUnreadCount, markAllNotificationsRead, markNotificationRead, searchUsers, clearAuthData, resolveImageUrl, getSupportTicketStats, createSupportTicket, getMyRsvps, getUnreadMessageCount } from "@/lib/api";
import { useLiturgicalTheme } from "@/contexts/LiturgicalThemeContext";
import { getSeasonLogo } from "@/lib/liturgicalCalendar";
import { toast } from "sonner";

function NotificationBell({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        getNotifications(),
        getUnreadCount()
      ]);
      setNotifications(notifRes.data.slice(0, 10));
      setUnreadCount(countRes.data.unread_count);
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setUnreadCount(0);
      loadNotifications();
    } catch {}
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        await markNotificationRead(notification.notification_id);
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {}
    }
    
    // Close dropdown
    setIsOpen(false);
    
    // Navigate based on notification type or link
    const link = getNotificationLink(notification);
    if (link) {
      navigate(link);
    }
  };

  const getNotificationLink = (notification) => {
    // If notification has a specific deep link (not just /dashboard), use it
    if (notification.link && notification.link !== '/dashboard') {
      return notification.link;
    }
    
    // Otherwise, determine link from notification type
    const type = notification.type || '';
    
    switch (type) {
      case 'message':
      case 'new_message':
        return '/messages';
      case 'family_request':
      case 'family_accepted':
      case 'family_invite':
        return '/community';
      case 'friend_request':
      case 'friend_accepted':
        return notification.from_user_id ? `/profile/${notification.from_user_id}` : '/community';
      case 'like':
      case 'comment':
      case 'mention':
      case 'repost':
      case 'post_like':
      case 'post_comment':
      case 'post_mention':
        return '/dashboard';
      case 'prayer_request':
      case 'prayer_update':
        return '/requests';
      case 'parish_invite':
      case 'parish_join':
        return '/parishes';
      case 'membership_request':
        return '/parish-admin';
      case 'membership_approved':
        return '/dashboard';
      case 'event_reminder':
      case 'event_invite':
        return '/dashboard';
      case 'marketplace_order':
      case 'order_update':
        return '/marketplace/orders';
      case 'group_invite':
      case 'group_post':
        return '/groups';
      case 'challenge_invite':
        return '/my-rule-of-life';
      case 'admin_assigned':
        return '/parish-admin';
      case 'announcement':
        return '/dashboard';
      default:
        return notification.link || '/dashboard';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
      case 'new_message':
        return <MessageCircle className="w-4 h-4 text-blue-500" />;
      case 'family_request':
      case 'family_accepted':
      case 'family_invite':
      case 'friend_request':
      case 'friend_accepted':
        return <Heart className="w-4 h-4 text-pink-500" />;
      case 'like':
      case 'post_like':
        return <Heart className="w-4 h-4 text-red-500" />;
      case 'comment':
      case 'post_comment':
        return <MessageSquare className="w-4 h-4 text-green-500" />;
      case 'mention':
      case 'post_mention':
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'repost':
        return <MessageSquare className="w-4 h-4 text-teal-500" />;
      case 'prayer_request':
      case 'prayer_update':
        return <HandHeart className="w-4 h-4 text-purple-500" />;
      case 'parish_invite':
      case 'parish_join':
      case 'membership_request':
      case 'membership_approved':
      case 'admin_assigned':
        return <Church className="w-4 h-4 text-[var(--liturgical-primary)]" />;
      case 'group_invite':
      case 'group_post':
        return <UsersRound className="w-4 h-4 text-orange-500" />;
      case 'challenge_invite':
        return <UsersRound className="w-4 h-4 text-amber-500" />;
      case 'announcement':
        return <Megaphone className="w-4 h-4 text-amber-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-[#F0EBE0] hover:bg-[#E5E0D5]" data-testid="notification-bell" title="Notifications">
          <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-[#2D2A26]" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-[8px] sm:text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-white border-[#E5E0D5]">
        <div className="flex items-center justify-between p-3 border-b border-[#E5E0D5]">
          <h3 className="font-['Crimson_Text'] font-semibold text-[#2D2A26]">Notifications</h3>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs text-[var(--liturgical-primary)] h-7">
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[300px]">
          {notifications.length === 0 ? (
            <p className="text-center text-sm text-[#6B665E] py-8 font-['Nunito']">No notifications yet</p>
          ) : (
            notifications.map((n, i) => (
              <div 
                key={n.notification_id || i} 
                className={`p-3 border-b border-[#E5E0D5]/50 hover:bg-[#F9F7F2] cursor-pointer transition-colors ${!n.read ? 'bg-[var(--liturgical-primary)]/5' : ''}`}
                onClick={() => handleNotificationClick(n)}
                data-testid={`notification-item-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(n.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-['Nunito'] text-[#2D2A26] line-clamp-2">{n.message}</p>
                    <p className="text-xs text-[#6B665E] mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                  </div>
                  {!n.read && (
                    <div className="w-2 h-2 bg-[var(--liturgical-primary)] rounded-full flex-shrink-0 mt-1.5" />
                  )}
                </div>
              </div>
            ))
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AdminTicketNotification({ user, navigate }) {
  const [ticketStats, setTicketStats] = useState({ open: 0, in_progress: 0 });
  
  useEffect(() => {
    if (!user?.is_site_admin) return;
    
    const loadStats = async () => {
      try {
        const res = await getSupportTicketStats();
        setTicketStats(res.data);
      } catch {}
    };
    
    loadStats();
    const interval = setInterval(loadStats, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [user?.is_site_admin]);

  const unresolvedCount = (ticketStats.open || 0) + (ticketStats.in_progress || 0);

  if (!user?.is_site_admin || unresolvedCount === 0) return null;

  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative h-10 w-10 rounded-full bg-amber-50 hover:bg-amber-100"
      onClick={() => navigate("/admin?tab=support")}
      data-testid="admin-ticket-notification"
      title={`${unresolvedCount} unresolved support ticket${unresolvedCount !== 1 ? 's' : ''}`}
    >
      <Ticket className="w-5 h-5 text-amber-600" />
      <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
        {unresolvedCount > 9 ? "9+" : unresolvedCount}
      </span>
    </Button>
  );
}

function SupportButton({ user }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const supportTypes = [
    { id: "bug", label: "Something is broken", icon: Bug, color: "text-red-500", bgColor: "bg-red-50 hover:bg-red-100" },
    { id: "feature", label: "Request a feature", icon: Lightbulb, color: "text-amber-500", bgColor: "bg-amber-50 hover:bg-amber-100" },
    { id: "other", label: "Other", icon: MessageSquare, color: "text-blue-500", bgColor: "bg-blue-50 hover:bg-blue-100" }
  ];

  const handleSubmit = async () => {
    if (!selectedType || !description.trim()) {
      toast.error("Please select a type and provide a description");
      return;
    }
    
    setSubmitting(true);
    try {
      await createSupportTicket({
        category: selectedType,
        subject: subject.trim() || `${supportTypes.find(t => t.id === selectedType)?.label}`,
        message: description.trim()
      });
      toast.success("Support request submitted! We'll get back to you soon.");
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setSelectedType(null);
    setSubject("");
    setDescription("");
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-10 w-10 rounded-full bg-[#F0EBE0] hover:bg-[#E5E0D5]" 
            data-testid="support-button"
            title="Support"
          >
            <HelpCircle className="w-5 h-5 text-[#2D2A26]" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-white border-[#E5E0D5]">
          <div className="px-3 py-2 border-b border-[#E5E0D5]">
            <p className="font-['Crimson_Text'] font-semibold text-[#2D2A26]">Need Help?</p>
            <p className="text-xs text-[#6B665E] font-['Nunito']">Let us know how we can help</p>
          </div>
          {supportTypes.map((type) => (
            <DropdownMenuItem
              key={type.id}
              onClick={() => { setSelectedType(type.id); setDialogOpen(true); }}
              className={`cursor-pointer font-['Nunito'] mx-1 my-0.5 rounded-md ${type.bgColor}`}
              data-testid={`support-option-${type.id}`}
            >
              <type.icon className={`w-4 h-4 mr-2 ${type.color}`} />
              <span className="text-[#2D2A26]">{type.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Support Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
              {selectedType && (() => {
                const type = supportTypes.find(t => t.id === selectedType);
                const Icon = type?.icon;
                return Icon ? <Icon className={`w-5 h-5 ${type.color}`} /> : null;
              })()}
              {supportTypes.find(t => t.id === selectedType)?.label}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Type Selection (if not pre-selected) */}
            {!selectedType && (
              <div className="space-y-2">
                <label className="text-sm font-['Nunito'] text-[#2D2A26] font-medium">What can we help with?</label>
                <div className="grid gap-2">
                  {supportTypes.map((type) => (
                    <Button
                      key={type.id}
                      variant="outline"
                      onClick={() => setSelectedType(type.id)}
                      className={`justify-start h-12 border-[#E5E0D5] ${selectedType === type.id ? 'border-[var(--liturgical-primary)] bg-[var(--liturgical-primary)]/5' : ''}`}
                    >
                      <type.icon className={`w-5 h-5 mr-3 ${type.color}`} />
                      <span className="font-['Nunito']">{type.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Subject (optional) */}
            <div className="space-y-2">
              <label className="text-sm font-['Nunito'] text-[#2D2A26] font-medium">Subject (optional)</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={selectedType === "bug" ? "e.g., Login page not loading" : selectedType === "feature" ? "e.g., Dark mode" : "Brief summary"}
                className="border-[#E5E0D5] font-['Nunito'] focus-visible:ring-[var(--liturgical-primary)]"
                data-testid="support-subject-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-['Nunito'] text-[#2D2A26] font-medium">
                {selectedType === "bug" ? "What happened?" : selectedType === "feature" ? "Describe your idea" : "How can we help?"} *
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  selectedType === "bug" 
                    ? "Please describe what went wrong and what you expected to happen..." 
                    : selectedType === "feature"
                    ? "Tell us about the feature you'd like to see..."
                    : "Please describe your question or concern..."
                }
                className="min-h-[120px] border-[#E5E0D5] font-['Nunito'] resize-none focus-visible:ring-[var(--liturgical-primary)]"
                data-testid="support-description-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setDialogOpen(false); resetForm(); }}
              className="border-[#E5E0D5] font-['Nunito']"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !selectedType || !description.trim()}
              className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
              data-testid="support-submit-btn"
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RsvpEventsIcon() {
  const navigate = useNavigate();
  const [upcomingCount, setUpcomingCount] = useState(0);

  useEffect(() => {
    const loadRsvps = async () => {
      try {
        const res = await getMyRsvps();
        const now = new Date();
        const upcoming = (res.data.rsvps || []).filter(r => new Date(r.event_date) >= now);
        setUpcomingCount(upcoming.length);
      } catch (err) {
        console.error("Failed to load RSVPs:", err);
      }
    };
    loadRsvps();
  }, []);

  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-[#F0EBE0] hover:bg-[#E5E0D5]"
      onClick={() => navigate("/calendar")}
      data-testid="nav-rsvp-btn"
      title="Calendar"
    >
      <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#2D2A26]" />
      {upcomingCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 sm:w-5 sm:h-5 bg-[var(--liturgical-primary)] text-white text-[8px] sm:text-[10px] font-bold rounded-full flex items-center justify-center">
          {upcomingCount > 9 ? "9+" : upcomingCount}
        </span>
      )}
    </Button>
  );
}

function MessagesButton() {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadUnreadCount();
    // Poll for new messages every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const res = await getUnreadMessageCount();
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error("Failed to load unread message count:", err);
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-[#F0EBE0] hover:bg-[#E5E0D5] relative"
      onClick={() => navigate("/messages")}
      data-testid="nav-messages-btn"
      title="Messages"
    >
      <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#2D2A26]" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </Button>
  );
}

export default function TopHeader({ user }) {
  const navigate = useNavigate();
  const { season } = useLiturgicalTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timer = setTimeout(async () => {
        try {
          const res = await searchUsers(searchQuery);
          setSearchResults(res.data.users?.slice(0, 5) || []);
          setShowSearch(true);
        } catch {}
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
      setShowSearch(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch {}
    clearAuthData();
    navigate("/login");
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery.trim()) {
      setShowSearch(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  const mobileLinks = [
    { path: "/dashboard", icon: Home, label: "Home" },
    { path: "/marketplace", icon: ShoppingBag, label: "Marketplace" },
    { path: "/directory", icon: Building2, label: "Business Directory" },
    { path: "/groups", icon: UsersRound, label: "Groups" },
    { path: "/community", icon: Users, label: "Community" },
    { path: "/sacrament-finder", icon: MapPinned, label: "Sacrament Finder" },
    { path: "/requests", icon: HandHeart, label: "Offers & Requests" },
    { path: "/my-rule-of-life", icon: ListChecks, label: "Rule of Life" },
    { path: "/saint-of-the-day", icon: HaloIcon, label: "Saint of the Day" },
  ];

  const mobileBottomLinks = [
    { path: "/saved", icon: Bookmark, label: "Saved" },
    { path: "/settings", icon: Settings, label: "Settings" },
    { path: "/support", icon: HelpCircle, label: "Support" },
  ];

  return (
    <>
      <header className="bg-white border-b border-[#E5E0D5] sticky top-0 z-50 h-14" data-testid="top-header">
        <div className="h-full px-4 flex items-center justify-between gap-4">
          {/* Left: Logo */}
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="header-logo">
              <img 
                src={getSeasonLogo(season)} 
                alt="Parish" 
                className="w-10 h-10 rounded-lg"
              />
              <span className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] hidden sm:block">Parish</span>
            </Link>
          </div>

          {/* Center: Search (desktop) */}
          <div className="hidden md:block flex-1 max-w-md relative" ref={searchRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
              <Input
                placeholder="Search Parish..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pl-10 bg-[#F0EBE0] border-0 focus-visible:ring-1 focus-visible:ring-[var(--liturgical-primary)] rounded-full h-10 font-['Nunito']"
                data-testid="header-search"
              />
            </div>
            {showSearch && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E5E0D5] rounded-lg shadow-lg overflow-hidden z-50">
                {searchResults.map((result) => (
                  <Link
                    key={result.user_id}
                    to={`/profile/${result.user_id}`}
                    onClick={() => { setShowSearch(false); setSearchQuery(""); }}
                    className="flex items-center gap-3 p-3 hover:bg-[#F9F7F2] transition-colors"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={resolveImageUrl(result.picture)} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">{result.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-['Nunito'] text-sm font-medium text-[#2D2A26]">{result.name}</p>
                      {result.parish_name && <p className="text-xs text-[#6B665E]">{result.parish_name}</p>}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Messages Button - visible on all screens */}
            <MessagesButton />
            
            {/* RSVP Events Calendar - visible on all screens */}
            <RsvpEventsIcon />
            
            {/* Support Button - hidden on mobile to save space */}
            <div className="hidden sm:block">
              <SupportButton user={user} />
            </div>
            
            {/* Admin Support Ticket Notification */}
            <AdminTicketNotification user={user} navigate={navigate} />

            {/* Notifications */}
            <NotificationBell user={user} />

            {/* Profile Dropdown (desktop) */}
            <div className="hidden md:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full p-0" data-testid="header-profile">
                    <Avatar className="h-10 w-10 border-2 border-[#E5E0D5]">
                      <AvatarImage src={resolveImageUrl(user?.picture)} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white font-['Nunito']">
                        {user?.name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white border-[#E5E0D5]">
                  <DropdownMenuItem onClick={() => navigate(`/profile/${user?.user_id}`)} className="cursor-pointer font-['Nunito']">
                    <User className="w-4 h-4 mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer font-['Nunito']">
                    <Settings className="w-4 h-4 mr-2" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/saved")} className="cursor-pointer font-['Nunito']">
                    <Bookmark className="w-4 h-4 mr-2" /> Saved
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 font-['Nunito']">
                    <LogOut className="w-4 h-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-10 w-10 rounded-full bg-[#F0EBE0] hover:bg-[#E5E0D5]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 top-14 bg-white z-40 md:hidden overflow-y-auto pb-20">
          <div className="p-4 pt-10 space-y-1">
            {/* User Profile */}
            <Link 
              to={`/profile/${user?.user_id}`}
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[#F0EBE0]"
            >
              <Avatar className="h-10 w-10 border border-[#E5E0D5]">
                <AvatarImage src={resolveImageUrl(user?.picture)} />
                <AvatarFallback className="bg-[var(--liturgical-primary)] text-white">{user?.name?.[0]}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-['Nunito'] font-semibold text-[#2D2A26]">{user?.name}</p>
                <p className="text-xs text-[#6B665E]">View profile</p>
              </div>
            </Link>

            {/* Mobile Search */}
            <div className="relative px-1 py-2">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
              <Input
                placeholder="Search Parish..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { handleSearch(e); setMobileMenuOpen(false); } }}
                className="pl-10 bg-[#F0EBE0] border-0 rounded-full h-10 font-['Nunito']"
                data-testid="mobile-header-search"
              />
            </div>

            <div className="border-t border-[#E5E0D5] my-2" />

            {/* My Parish link(s) */}
            {user?.parishes && user.parishes.length > 0 ? (
              user.parishes.map((p) => (
                <Link
                  key={p.parish_id}
                  to={`/parish/${p.parish_id}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[#F0EBE0] font-['Nunito'] text-[#2D2A26]"
                >
                  <Church className="w-5 h-5 text-[var(--liturgical-primary)]" />
                  <span>{p.name || "My Parish"}</span>
                </Link>
              ))
            ) : user?.parish_id ? (
              <Link
                to={`/parish/${user.parish_id}`}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[#F0EBE0] font-['Nunito'] text-[#2D2A26]"
              >
                <Church className="w-5 h-5 text-[var(--liturgical-primary)]" />
                <span>{user.parish_name || "My Parish"}</span>
              </Link>
            ) : null}
            <div className="border-t border-[#E5E0D5] my-2" />

            {/* Main Navigation */}
            {mobileLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[#F0EBE0] font-['Nunito'] text-[#2D2A26]"
              >
                <link.icon className="w-5 h-5 text-[#6B665E]" />
                <span>{link.label}</span>
              </Link>
            ))}

            {/* Admin Links */}
            {(user?.role === "admin" || user?.role === "site_admin" || user?.is_site_admin || user?.is_parish_admin) && (
              <>
                <div className="border-t border-[#E5E0D5] my-2" />
                <p className="px-3 text-xs font-['Nunito'] text-[#6B665E] uppercase">Admin</p>
                {(user?.role === "admin" || user?.role === "site_admin" || user?.is_site_admin) && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[#F0EBE0] font-['Nunito'] text-[#2D2A26]"
                  >
                    <Shield className="w-5 h-5 text-[#6B665E]" />
                    <span>Site Admin</span>
                  </Link>
                )}
                {user?.is_parish_admin && (
                  <Link
                    to={`/parish/${user.parish_id}/admin`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[#F0EBE0] font-['Nunito'] text-[#2D2A26]"
                  >
                    <Church className="w-5 h-5 text-[#6B665E]" />
                    <span>Parish Admin</span>
                  </Link>
                )}
              </>
            )}

            {/* Bottom links: Saved, Settings, Support */}
            <div className="border-t border-[#E5E0D5] my-2" />
            {mobileBottomLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[#F0EBE0] font-['Nunito'] text-[#6B665E]"
              >
                <link.icon className="w-5 h-5" />
                <span>{link.label}</span>
              </Link>
            ))}

            <div className="border-t border-[#E5E0D5] my-2" />

            {/* Logout */}
            <button
              onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-red-50 font-['Nunito'] text-red-600 w-full text-left"
            >
              <LogOut className="w-5 h-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
